<template>
	<article :class="{ 'companies-list-container full-width page-container': true }">
		<div class="filter-container">
			<div class="entity-switches" >
				<button type="button" :class="{ 'button btn-entity-switch': true, 'selected': selectedEntities === 'companies' }" @click="selectEntity('companies')">{{ $t('companyList.destinations') }}</button>
				<button type="button" :class="{ 'button btn-entity-switch': true, 'selected': selectedEntities === 'bonusClub' }" @click="selectEntity('bonusClub')">{{ $t('companyList.bonusClub') }}</button>
			</div>

			<div class="search-and-actions-container">
				<div class="input-group search-group">
					<input class="input-group-field search-input" :placeholder="$t('filterLabels.searchPlaceholder')" type="text" v-model="searchText"  @keyup="doSearch">
					<div class="input-group-button">
						<font-awesome-icon :icon="['far', 'search']" />
					</div>
				</div>

				<div class="sort-group">
					<select class="category-select" v-model="sortedBy">
						<option value="" disabled="true">{{ $t('sortAfter') }}</option>
						<option value="alphabetical-asc">{{ $t('sorting.asc') }}</option>
						<option value="alphabetical-desc">{{ $t('sorting.desc') }}</option>
						<option value="distance">{{ $t('sorting.distance') }}</option>
					</select>
				</div>

				<div class="card-button-wrapper">
					<router-link class="button btn-map hollow" :to="{ name: 'map', params: { 'entity': this.selectedEntities } }">
						<font-awesome-icon :icon="['fal', 'map']" />
						<span class="map-label">
							{{ $t('buttonActions.map') }}
						</span>
					</router-link>
				</div>

				<div class="filter-wrapper">
					<button type="button" :class="{'button btn-filter hollow': true, 'active-filter': isResultFiltered}" @click="openFilterReveal">
						<font-awesome-icon :icon="['fal', 'sliders-h']" />
					</button>
				</div>
			</div>
		</div>
		
		<ul v-if="isLoading" style="list-style: none;">
			<li style="width: 90%"><vcl-facebook></vcl-facebook></li>
			<li style="width: 90%"><vcl-facebook></vcl-facebook></li>
			<li style="width: 90%"><vcl-facebook></vcl-facebook></li>
			<li style="width: 90%"><vcl-facebook></vcl-facebook></li>
		</ul>

		<keep-alive>
			<ul class="companies-list" v-show="results.length > 0">
				<template v-for="(company, index) in results">
					<company-item :item="company" :key="`company-${company.id}`" />

					<top-performance-slider :items="randomTopPerformanceItems" v-if="showTPSlider(index)" :key="`tp-slider-${index}-${$store.state.language}-${$store.state.season}`" :switched-entities="switchedEntities || filtered" v-on:tp-slider-reinit="resetEntitySwitch" />
				</template>
			</ul>
		</keep-alive>

		<div class="no-entities label" v-if="false">
			{{ $t('nothingFound') }}
		</div>
	
		<filter-overlay
			:key="`filter-reveal-${selectedEntities}`"
			ref="companyListFilterReveal"
			:entities="selectedEntities"
			enable-categories
			enable-criterias
			enable-range
			enable-top-performance
			:entity-categories="entityCategories"
			:criterias="criterias"
			v-on:close-reveal="closeFilterReveal"
		/>

	</article>
</template>

<script>
	import foundation from '@/mixins/foundation'
	import checkforupdates from '@/mixins/checkforupdates.js'
	import {
		mapActions as mapSearchActions,
		mapGetters as mapSearchGetters,
		getterTypes,
		actionTypes,
	} from 'vuex-search';
	import { VclFacebook } from 'vue-content-loading';


	export default {
		name: 'CompaniesList',
		components: {
			CompanyItem: () => import('@/components/lists/CompanyItem.vue'),
			TopPerformanceSlider: () => import ('@/components/TopPerformanceSlider.vue'),
			FilterOverlay: () => import('@/components/FilterOverlay.vue'),
			VclFacebook
		},
		mixins: [ foundation, checkforupdates ],
		data () {
			return {
				isLoading: true,
				selectedEntities: 'companies', // either companies or bonus-club,
				switchedEntities: false,
				searchText: '',
				sortedBy: 'distance',
				randomTopPerformanceItems: [],
				results: [],
				entityCategories: undefined,
				filtered: false
			}
		},
		metaInfo() {
			return {
				title: this.$t('services') + ' | ' + 'Kärnten Card App',
				meta: [{
					property: 'og:title', content: + this.$t('services') + ' | ' + 'Kärnten Card App' 
				}, {
					property: 'og:site_name', content: 'Kärnten Card App'
				}, {
					property: 'og:url', content: window.location.href
				}, {
					property: 'og:image', content: "https://app.kaerntencard.at/img/kc-logo.png"
				},{
					property: 'og:description', content: this.$t('servicesDescription')
				}]
			}
		},
		computed: {
			...mapSearchGetters('companies', {
				companiesResultIds: getterTypes.result,
				isSearchingCompanies: getterTypes.isSearching
			}),
			bonusCategories() {
				return this.$store.getters.getCurrentBonusCategories
			},
			categories () {
				return this.$store.getters.getCurrentCategories
			},
			criterias () {
				return this.$store.getters.getCurrentCriterias
			},
			selectedRange () {
				return this.$store.getters.getSelectedRange(this.selectedEntities)
			},
			selectedCategories () {
				return this.$store.getters.getSelectedCategories(this.selectedEntities)
			},
			selectedCriterias () {
				return this.$store.getters.getSelectedCriterias(this.selectedEntities)
			},
			allowOnlyTopPerformanceEntities () {
				return this.$store.getters.getTopPerformanceState(this.selectedEntities)
			},
			isResultFiltered () {
				if (this.selectedCategories.length >= 1) {
					return true
				}

				if (this.selectedCriterias.length >= 1) {
					return true
				}

				if (this.selectedRange <= 140) {
					return true
				}

				if (this.allowOnlyTopPerformanceEntities) {
					return true
				}

				return false
			}
		},
		created () {
			// console.log('entered the created() lifecycle hook of CompaniesList.vue')
			this.initView()
		},
		methods: {
			...mapSearchActions ('companies', {
				searchCompanies: actionTypes.search
			}),
			openFilterReveal() {
				this.$refs.companyListFilterReveal.$el.classList.add('is-reveal-open')
			},
			closeFilterReveal () {
				this.$refs.companyListFilterReveal.$el.classList.remove('is-reveal-open')
			},
			initView () {
				// console.log('current location in store is: lat '+this.$store.state.currentPosition.lat+' long '+this.$store.state.currentPosition.lng+' start requestCurrent Position.')
				this.requestCurrentPosition()
				.then((pos) => {
					console.log('New location is: lat '+pos.lat+' long '+pos.lng)
					this.$store.dispatch('updateCurrentPosition', pos)
					this.$store.dispatch('fetchNearbyCompanies', {
						lang: this.$store.state.language,
						season: this.$store.state.season,
						coords: this.$store.state.currentPosition
					})
				})
				.catch(err => {
					console.error(err)
					if (this.$store.state.currentPosition.lat !== undefined && this.$store.state.currentPosition.lng !== undefined) {
						this.$store.dispatch('fetchNearbyCompanies', {
							lang: this.$store.state.language,
							season: this.$store.state.season,
							coords: this.$store.state.currentPosition
						})
					} else {
						this.$store.dispatch('fetchCompanies', { lang: this.$i18n.locale, season: this.$store.state.season })
							.then(() => {
								this.sortedBy = ''
							})
					}
				})
				this.$store.dispatch('fetchCategories', { lang: this.$store.state.lang, season: this.$store.state.season })
				this.$store.dispatch('fetchCriterias', { lang: this.$store.state.lang, season: this.$store.state.season })
				this.$store.dispatch('fetchBonusCategories', { lang: this.$store.state.lang, season: this.$store.state.season })
			},
			searchAndFilterCompanies () {
				let companies = []
				// console.log('start loading companies')
				this.filtered = true

				if(this.selectedEntities === 'companies'){
					this.companiesResultIds.forEach(id => {
						let company = this.$store.getters.getCompanyByID(id)
						if (company && company.designation.includes('Ausflugsziel')) {
							companies.push(company)
						}
					})
				} else if (this.selectedEntities === 'bonusClub') {
					this.companiesResultIds.forEach(id => {
						let company = this.$store.getters.getCompanyByID(id)
						if (company && company.designation.includes('Bonuspartner')) {
							companies.push(company)
						}
					})
				} 
				// console.log('loaded companies', companies.length, companies)


				// filter for categories
				if (this.selectedCategories.length >= 1 && companies.length >= 1) {
					if (this.selectedEntities === 'companies') {
						companies = companies.filter((company) => {
							if (this.selectedCategories.filter(cat => {
								return cat.name === company.category
							}).length >= 1) {
								return true
							} else {
								return false
							}
						});
						console.log('filtered result with categories')
					} else {
						companies = companies.filter((company) => {
							if (this.selectedCategories.filter(cat => {
								return cat.name === company.bonus_category
							}).length >= 1) {
								return true
							} else {
								return false
							}
						});
						console.log('filtered result with bonus categories')
					}
				}

				// filter for criterias
				if (this.selectedCriterias.length >= 1 && companies.length >= 1) {
					// 1st comparrison array --> selected criteria namse
					let selectedCriteriaNames = this.selectedCriterias.map(criteria => {
						return criteria.name
					})

					// check if company criterias are present in the 1st comparrison array
					companies = companies.filter(company => {
						let criteriaMatches = []

						company.criteria.forEach(criteria => {
							if (selectedCriteriaNames.includes(criteria)) {
								criteriaMatches.push(criteria)
							}
						})

						if (criteriaMatches.length === selectedCriteriaNames.length) {
							return true
						} else {
							return false 
						}
					})
					console.log('filtered result with criterias')
				}

				// filter for current range if companies have distance set
				if (this.selectedRange <= 140 && companies.length >= 1) {
					companies = companies.filter((company) => {
						if (company.distance !== undefined) {
							return company.distance <= this.selectedRange * 1000
						}
					})
				}

				// get random top perfomance items based on filter result
				let topPerformanceItems = companies.filter(company => {
					return company.isTopPerformance
				})

				// filter for top performance companies
				if (this.allowOnlyTopPerformanceEntities) {
					companies = topPerformanceItems
				}

				topPerformanceItems = this.shuffleResult(topPerformanceItems)
				// console.log(`Found ${topPerformanceItems.length} Top Performance Companies.`)

				this.randomTopPerformanceItems = topPerformanceItems
				this.results = this.sortResult(companies)
				this.isLoading = false
			},
			selectEntity (newEntity) {
				this.selectedEntities = newEntity

				if (newEntity === 'companies') {
					this.entityCategories = this.categories
				} else {
					this.entityCategories =  this.bonusCategories
				}
			},
			doSearch () {
				this.searchCompanies(this.searchText)
			},
			sortResult (result) {
				if (this.sortedBy === '') {
					return result
				}

				if (this.sortedBy === 'alphabetical-asc') {
					return result.sort((a, b) => {
						if (a.title < b.title) {
							return -1
						}

						if(a.title > b.title) {
							return 1
						}

						return 0
					})
				}

				if (this.sortedBy === 'alphabetical-desc') {
					return result.sort((a, b) => {
						if (a.title > b.title) {
							return -1
						}
						
						if(a.title < b.title) {
							return 1
						}

						return 0
					})
				}

				if (this.sortedBy === 'distance') {
					return result.sort((a, b) => {
						if (a.distance < b.distance) {
							return -1
						}

						if (a.distance > b.distance) {
							return 1
						}

						return 0
					})
				}

				// always return the filter/search/sort result if no sorting happenes
				return result
			},
			shuffleResult (result) {
				// eslint-disable-next-line
				return result.sort((a, b) => Math.random() > .5 ? -1 : 1);
			},
			showTPSlider (currentIndex) {
				if (this.randomTopPerformanceItems.length <= 0) {
					return false
				}

				if (this.results.length < 9 && (currentIndex === this.results.length - 1)) {
					return true
				} else if (this.results.length >= 9 && currentIndex === 9) {
					return true
				}

				return false
			},
			resetEntitySwitch () {
				this.switchedEntities = false
				this.filtered = false
			}
		},
		watch: {
			companiesResultIds: function () {
				this.searchAndFilterCompanies()
			},
			selectedEntities: function (newValue, oldValue) {
				this.searchAndFilterCompanies()

				this.$store.dispatch('updateEntity', newValue)

				// trigger re-init of top performance slider
				if (newValue !== oldValue){
					this.switchedEntities = true
				} else {
					this.switchedEntities = false
				}
			},
			categories: function(newCatArray) {
				if (this.selectedEntities === 'companies') {
					this.entityCategories = newCatArray
				}
			},
			bonusCategories: function (newCatArray) {
				if (this.selectedEntities === 'bonusClub') {
					this.entityCategories = newCatArray
				}
			},
			sortedBy: function () {
				this.searchAndFilterCompanies()
			},
			'selectedCategories': function () {
				this.searchAndFilterCompanies()
			},
			'selectedCriterias': function () {
				this.searchAndFilterCompanies()
			},
			'selectedRange': function () {
				this.searchAndFilterCompanies()
			},
			'allowOnlyTopPerformanceEntities': function () {
				this.searchAndFilterCompanies()
			},
			'$store.state.season': function () {
				this.initView()
				this.searchAndFilterCompanies()
				this.$store.dispatch('fetchCardInfos', { lang: this.$store.state.language, season: this.$store.state.season })
					.then(() => {
						this.$store.dispatch('fetchNews', { lang: this.$store.state.language, season: this.$store.state.season })
					})
					.catch(err => console.log(err))
			},
			'$store.state.language': function () {
				this.initView()
				this.searchAndFilterCompanies()
				this.$store.dispatch('fetchCardInfos', { lang: this.$store.state.language, season: this.$store.state.season })
					.then(() => {
						this.$store.dispatch('fetchNews', { lang: this.$store.state.language, season: this.$store.state.season })
					})
					.catch(err => console.log(err))
			},
			results: {
				handler: function(t){
					// console.log('results', t)
					this.results = t
					this.$store.dispatch('updateFilteredResultsCount', { entity: this.selectedEntities, count: this.results.length})
					this.$store.dispatch('updateFilteredResults', { entity: this.selectedEntities, results: this.results })
					this.isLoading = false
				},
				deep: true
			}
		},
		mounted () {
			this.selectedEntities = this.$store.state.lastEntity

			if (this.selectedEntities === 'companies') {
				this.entityCategories = this.categories
			} else {
				this.entityCategories =  this.bonusCategories
			}
		}
	}
</script>

<style lang="scss" scoped>
	.reveal{
		overflow: hidden;
	}
	.companies-list-container{
		.filter-container{
			background-color: $white;
			box-shadow: 0px 0px 2px rgba($black, .15);

			.entity-switches{
				@include xy-grid();
				width: 100%;
				display: flex;
				justify-content: center;
				align-items: center;

				.btn-entity-switch{
					@include xy-cell(6, false, 0);
					margin: 0;
					border: 1px solid $light-gray;

					&:not(.selected) {
						color: $dark-gray;
						background-color: $white;
					}
				}
			}
			.search-and-actions-container{
				@include xy-grid();
				@include xy-gutters($gutter-type: padding, $gutter-position: left right top bottom);
				margin-bottom: .5rem;
				border-bottom: 1px solid $light-gray;

				.search-group{
					@include xy-cell(12, false, 0);
					display: flex;
					justify-content: center;
					align-items: center;
					margin: 0;
					color: $dark-gray;
					border: 1px solid $medium-gray;
					border-radius: 20rem;

					.search-input{
						height: auto;
						padding: 1rem;
						color: $dark-gray;
						background: none;
						border: none;
						box-shadow: none;
					}
					.input-group-button{
						margin-right: 1.5rem;
						font-size: 1.5rem;
					}
				}

				.sort-group{
					@include xy-cell(6, false, 0);
					margin-top: 1rem;

					.category-select{
						margin: 0;
						padding: .5rem 1rem;
						padding-right: 2rem;
						color: $dark-gray;
						border-radius: 20rem;
						align-items: flex-end;
					}
				}

				.card-button-wrapper{
					@include xy-cell(5, false, 0);
					display: flex;
					justify-content: center;
					align-items: center;
					margin-top: 1rem;

					.btn-map{
						display: inline-block;
						margin-bottom: 0;
						border-radius: 20rem;

						@include breakpoint(medium){
							width: 80%;
						}

						.map-label{
							@include breakpoint(small only) {
								display: none;
							}
						}
					}
				}

				.filter-wrapper{
					@include xy-cell(1, false, 0);
					display: flex;
					align-items: center;
					justify-content: flex-end;
					margin-top: 1rem;

					.btn-filter{
						margin: 0;
						color: $dark-gray;
						border: 1px solid $dark-gray;
						border-radius: 20rem;

						&:hover,
						&:focus {
							color: scale-color($dark-gray, $lightness: -15%);
							border: 1px solid scale-color($dark-gray, $lightness: -15%);
						}

						&.active-filter{
							color: $white;
							background-color: get-color(primary);
							border: 1px solid get-color(primary);
						}
					}
				}

				.active-filters-container{
					@include xy-cell(12);
				}
			}
		}

		.companies-list{
			@include xy-grid();
			@include xy-grid-layout(1, '.company-item', false, 0);
			list-style: none;
			margin: 0;
			padding: 0;

			@include breakpoint(medium) {
				@include xy-grid-layout(2, '.company-item');
			}

			@include breakpoint(large) {
				@include xy-grid-layout(2, '.company-item');
			}
		}

		.no-entities{
			display: block;
			width: 100%;
			max-width: rem-calc(350);
			margin: 0 auto;
			margin-top: 1rem;
			padding: .75rem 1.5rem !important;
			font-size: 1.15rem;
			text-align: center;
			border-radius: 20rem;
		}
	}
</style>